import React, {
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { updateTicketAssignee } from 'flow/actions/tickets-action';
import EmojiSearch from 'flow/components/messages/EmojiSearch';
import useAttachmentHandler from 'flow/components/tickets/UseAttachmentHandler';
import useAppContext from 'flow/hooks/useAppContext';
import useAutosizeTextArea from 'flow/hooks/useAutosizeTextArea';
import { featureFlagCheck } from 'flow/utils/feature-flag-utils';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { selectFeatureFlags } from 'flow/selectors/feature-flag-selectors';
import {
    selectEmailSignaturesEnabled,
    selectAssignee,
    selectHasAttachmentRestrictionsInStore,
    selectOriginDTOSubtype,
    selectTicketReplyDraft,
    selectTicketReplyDraftMentions,
    selectTicketsAllowedMimeTypesData,
    selectTicketsBlockedMimeTypes,
    selectWhatsAppEventsAreFetched,
    makeSelectRequiresWhatsAppTemplateReply,
} from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { twMerge } from 'tailwind-merge';
import Skeleton from 'flow/components/ui-components/Skeleton';
import { ChannelValues } from 'flow/components/tickets/messageChannels';

import { fetchTicketAttachmentRestrictions } from 'flow/components/tickets/Input/InputActions';
import AttachmentButton from 'flow/components/tickets/Input/AttachmentButton';
import EmojiPickerTickets from 'flow/components/tickets/Input/EmojiPickerTickets';
import InputFooterV2 from './InputFooterV2';
import TicketInputAttachmentPreview from 'flow/components/tickets/Input/TicketInputAttachmentPreview';
import InputLockIndicator from 'flow/components/tickets/Input/InputLockIndicator';
import { Tab } from 'flow/components/tickets/Input/InputTabs';
import {
    getPossibleMentionString,
    MentionPickerHandle,
} from 'flow/components/tickets/Input/MentionPicker/MentionPicker';
import TicketMentionPicker from 'flow/components/tickets/Input/MentionPicker/TicketMentionPicker';
import { useExternalMessagePlaceholder } from 'flow/components/tickets/Input/hooks/useExternalMessagePlaceholder';
import { useSendTicketMessage } from 'flow/components/tickets/Input/hooks/useSendTicketMessage';
import { useTicketInputKeyboard } from 'flow/components/tickets/Input/hooks/useTicketInputKeyboard';
import { useTicketMessageContent } from 'flow/components/tickets/Input/hooks/useTicketMessageContent';
import { useTicketComposerTab } from 'flow/components/tickets/Input/hooks/useTicketComposerTab';
import {
    getChannelSelected,
    replyChannelSupportsAttachments,
    useExternalReplyChannel,
} from 'flow/components/tickets/Input/hooks/useExternalReplyChannel';
import { useTextChangeTracking } from 'flow/components/tickets/Input/hooks/useTextChangeTracking';
import { useMentionAutoComplete } from 'flow/components/tickets/Input/hooks/useMentionAutoComplete';
import { useInputLockState } from 'flow/components/tickets/Input/hooks/useInputLockState';
import { useInputFocus } from 'flow/components/tickets/Input/hooks/useInputFocus';
import BannerEmailRecipient from 'flow/components/tickets/Input/Email/BannerEmailRecipient';
import { useDefaultPremadeAnswer } from 'flow/hooks/api/usePremadeAnswer';
import EmailSignaturePreview from './EmailSignaturePreview';
import { shouldApplyDefaultSignature } from './email-signature-utils';
import { PremadeAnswer } from 'flow/types/Chats';
import useTicketAction from 'flow/hooks/useTicketAction';

interface ComposerInputStateParams {
    whatsAppEventsLoading: boolean;
    disabled: boolean;
    tabSelected: 'internal' | 'reply_external';
    tabSelectedIsWritable: boolean;
    externalPlaceholder: string;
    internalPlaceholder: string;
}

// While a WhatsApp ticket's events are loading its reply state (whether a
// template reply is required) is unknown, so we keep the composer disabled and
// blank the placeholder -- the skeleton is shown in its place instead of a
// possibly-misleading prompt.
const getComposerInputState = ({
    whatsAppEventsLoading,
    disabled,
    tabSelected,
    tabSelectedIsWritable,
    externalPlaceholder,
    internalPlaceholder,
}: ComposerInputStateParams) => {
    const inputDisabled =
        disabled ||
        whatsAppEventsLoading ||
        // The tab the viewer is on may be one the backend refuses: a member
        // without the tickets licence may neither reply nor write a note.
        !tabSelectedIsWritable;
    const activePlaceholder =
        tabSelected === 'reply_external'
            ? externalPlaceholder
            : internalPlaceholder;
    const placeholder = whatsAppEventsLoading ? '' : activePlaceholder;

    // activePlaceholder as well: the name has to hold while the placeholder is
    // blanked, or the field loses it for exactly as long as the ticket loads.
    return { inputDisabled, placeholder, activePlaceholder };
};

// Overlays the whole input row (matching its background) so the empty, disabled
// textarea and the attachment button are hidden behind a single skeleton bar
// while a WhatsApp ticket's events are still loading.
const ComposerLoadingPlaceholder = ({ loading }: { loading: boolean }) => {
    if (!loading) return null;

    return (
        <div className="pointer-events-none absolute inset-0 flex items-center rounded-sm bg-gray-200 px-3">
            <Skeleton.Text className="h-3.5 w-40 text-gray-300" />
        </div>
    );
};

interface Props {
    ticketKey: TicketKey;
    disabled?: boolean;
    autoFocus?: boolean;
}
const MessageInputTicketV2 = ({
    ticketKey,
    disabled = false,
    autoFocus = true,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    const webclientFeatureFlags = useAppSelector(selectFeatureFlags);
    const userAllowedToSendAttachments = useMemo(
        () =>
            featureFlagCheck(
                webclientFeatureFlags,
                FeatureFlags.SEND_TICKET_ATTACHMENT,
            ),
        [webclientFeatureFlags],
    );

    const replyExternalDraft = useAppSelector((state) =>
        selectTicketReplyDraft(state, ticketKey, 'external'),
    );
    const internalDraft = useAppSelector((state) =>
        selectTicketReplyDraft(state, ticketKey, 'internal'),
    );
    const internalDraftMentions = useAppSelector((state) =>
        selectTicketReplyDraftMentions(state, ticketKey, 'internal'),
    );

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const hasAttachmentRestrictionsInStore = useAppSelector(
        selectHasAttachmentRestrictionsInStore,
    );

    useEffect(() => {
        if (hasAttachmentRestrictionsInStore) return;
        void dispatch(fetchTicketAttachmentRestrictions());
    }, [dispatch, hasAttachmentRestrictionsInStore]);

    const originSubtype = useAppSelector((state) =>
        selectOriginDTOSubtype(state, ticketKey),
    );

    const whatsAppEventsAreFetched = useAppSelector((state) =>
        selectWhatsAppEventsAreFetched(state, ticketKey),
    );
    const whatsAppEventsLoading = !whatsAppEventsAreFetched;

    const assignee = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );

    const { externalChannelSelected, setExternalChannelSelected } =
        useExternalReplyChannel(ticketKey, originSubtype);

    const selectRequiresWhatsAppTemplateReply = useMemo(
        makeSelectRequiresWhatsAppTemplateReply,
        [],
    );
    const whatsAppTemplateReplyRequired = useAppSelector((state) =>
        selectRequiresWhatsAppTemplateReply(state, ticketKey),
    );

    const inputFieldRef = useRef(null);
    const mentionPickerRef = useRef<MentionPickerHandle>(null);
    const [showMentionPicker, setShowMentionPicker] = useState(false);
    const [allowInputInExternalReply, setAllowInputInExternalReply] = useState(
        assignee === ownExtensionKey && !whatsAppTemplateReplyRequired,
    );
    const [unlockButtonClicked, setUnlockButtonClicked] = useState(false);

    const {
        messageContentInternal,
        setMessageContentInternal,
        messageContentReplyExternal,
        setMessageContentReplyExternal,
        updateMessageAndCaretPosition,
    } = useTicketMessageContent({
        ticketKey,
        internalDraft,
        internalDraftMentions,
        replyExternalDraft,
        inputFieldRef,
    });

    const [textField, setTextField] = useState(inputFieldRef?.current);

    const {
        tabSelected,
        setTabSelected,
        tabSelectedIsWritable,
        canReply,
        canAddInternalNote,
        originPending,
        replyUnsupported,
    } = useTicketComposerTab({
        ticketKey,
        originSubtype,
    });

    const channelSelected = getChannelSelected(
        tabSelected,
        externalChannelSelected,
    );

    const activeMessageContent =
        tabSelected === 'reply_external'
            ? messageContentReplyExternal
            : messageContentInternal;

    const attachmentButtonDisabled =
        !tabSelectedIsWritable ||
        (tabSelected === 'reply_external' && !allowInputInExternalReply);

    const channelSupportsAttachments =
        tabSelected === 'internal' ||
        replyChannelSupportsAttachments(externalChannelSelected);

    const allowedAttachmentsData = useAppSelector((state) =>
        selectTicketsAllowedMimeTypesData(state, channelSelected),
    );
    const blockedAttachmentsMimeTypes = useAppSelector((state) =>
        selectTicketsBlockedMimeTypes(state, channelSelected),
    );

    const [sentSegmentTracks, setSentSegmentTracks] = useState<Set<string>>(
        new Set(),
    );

    const resetSegmentTrackingFlags = useCallback(
        (channel: 'internal' | ChannelValues) => {
            setSentSegmentTracks((prev) => {
                const next = new Set(prev);
                next.delete(channel);
                return next;
            });
        },
        [],
    );

    const { trackTextChangeStart } = useTextChangeTracking({
        sentSegmentTracks,
        setSentSegmentTracks,
    });

    const { handleSendMessage } = useSendTicketMessage({
        ticketKey,
        tabSelectedIsWritable,
        messageContentInternal,
        messageContentReplyExternal,
        setMessageContentInternal,
        setMessageContentReplyExternal,
        resetSegmentTrackingFlags,
    });

    const showWhatsAppInactivityTemplateState =
        whatsAppTemplateReplyRequired &&
        (assignee === ownExtensionKey || unlockButtonClicked);

    const externalMessagePlaceholder = useExternalMessagePlaceholder({
        ticketKey,
        externalChannelSelected,
        allowInputInExternalReply,
        unlockButtonClicked,
    });

    const { inputDisabled, placeholder, activePlaceholder } =
        getComposerInputState({
            whatsAppEventsLoading,
            disabled,
            tabSelected,
            tabSelectedIsWritable,
            externalPlaceholder: externalMessagePlaceholder,
            internalPlaceholder: i18next.t('ticket.note_internal.write'),
        });

    // Feed the displayed placeholder (blank while a WhatsApp ticket loads, real
    // text after) so the height is re-measured once the textarea becomes enabled
    // -- otherwise the height measured during loading sticks and clips the
    // placeholder.
    useAutosizeTextArea(
        inputFieldRef,
        activeMessageContent?.text || placeholder,
    );

    const [isChatSwitched, setIsChatSwitched] = useState(false);

    useEffect(() => {
        if (assignee !== ownExtensionKey || whatsAppTemplateReplyRequired) {
            setAllowInputInExternalReply(false);
        } else {
            setAllowInputInExternalReply(true);
        }
    }, [assignee, ownExtensionKey, whatsAppTemplateReplyRequired]);

    useInputFocus({
        inputFieldRef,
        tabSelected,
        allowInputInExternalReply,
        isChatSwitched,
        setIsChatSwitched,
        ticketKey,
        autoFocus,
    });

    const closeMentionPicker = useCallback(() => {
        setShowMentionPicker(false);
    }, []);

    useEffect(() => {
        if (tabSelected !== 'internal') return;
        const text = messageContentInternal?.text || '';
        const textBeforeCaret = text.slice(0, textField?.selectionStart ?? 0);
        if (getPossibleMentionString(textBeforeCaret)) {
            setShowMentionPicker(true);
        } else {
            setShowMentionPicker(false);
        }
    }, [messageContentInternal?.text, tabSelected, textField?.selectionStart]);

    const getCaretPosition = () => {
        return textField?.selectionStart;
    };

    const setCaretPosition = (position: number) => {
        setMessageFieldFocus();

        // We need to set caret position and force a re-render in the next event loop since the call to focus
        // above sets the caret already. Without the timeout, caret position would always be set at end of text.
        setTimeout(() => {
            if (textField && textField.setSelectionRange) {
                textField.setSelectionRange(position, position);
            }
        });
    };

    const getPossibleMentionText = () => {
        const textTrimmedToCaret = messageContentInternal.text?.substring(
            0,
            getCaretPosition(),
        );

        return getPossibleMentionString(textTrimmedToCaret);
    };

    const handleTextChanged = (
        event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
        // Early return for external reply if not allowed
        if (tabSelected === 'reply_external' && !allowInputInExternalReply) {
            return;
        }

        const text = event.target.value;
        const caretPosition = event.target.selectionStart;

        updateMessageAndCaretPosition(text, caretPosition, tabSelected);
        setTextField(event.target);

        // Track user began writing
        trackTextChangeStart(channelSelected);
    };

    const onClickTextField =
        (/*event: React.MouseEvent<HTMLTextAreaElement>*/) => {
            setMessageFieldFocus();
            if (whatsAppTemplateReplyRequired) inputFieldRef?.current?.blur();
            if (
                tabSelected === 'reply_external' &&
                !allowInputInExternalReply
            ) {
                if (assignee === 'unassigned') {
                    runTicketAction(
                        updateTicketAssignee(ticketKey, ownExtensionKey),
                    );
                    dispatch(
                        segmentTrack(
                            TICKET_STRINGS.ASSIGN_TO_SELF_AND_REPLY_INPUT_CLICK,
                            {
                                component: 'Ticket input field',
                            },
                        ),
                    );
                } else {
                    inputFieldRef?.current?.blur();
                    return;
                }
            }
        };

    const setMessageFieldFocus = useCallback(() => {
        if (tabSelected === 'reply_external' && !allowInputInExternalReply)
            return;

        inputFieldRef.current?.focus();
    }, [tabSelected, allowInputInExternalReply, inputFieldRef]);

    const onClickTab = useCallback(
        (tab: Tab) => {
            setTabSelected(tab.key);
            setTextField(inputFieldRef.current);
            setMessageFieldFocus();
        },
        [setMessageFieldFocus],
    );

    const sendMessage = async () => {
        await handleSendMessage(channelSelected);
        setMessageFieldFocus();
    };

    const { handleKeyDown } = useTicketInputKeyboard({
        showMentionPicker,
        setShowMentionPicker,
        mentionPickerRef,
        tabSelected,
        messageContentInternal,
        messageContentReplyExternal,
        sendMessage,
    });

    const { handleMentionAutoComplete } = useMentionAutoComplete({
        messageContentInternal,
        setMessageContentInternal,
        getCaretPosition,
        setCaretPosition,
        setShowMentionPicker,
        sendMessage,
    });

    const onClickUnlockButton = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        dispatch(
            segmentTrack(TICKET_STRINGS.CLICKED_UNLOCK_TO_REPLY, {
                component: 'Ticket input field',
            }),
        );
        event.preventDefault();
        // preventDefault required or sometimes a '?' is added to the browser path, causing the page to reload.
        // This because the button is inside a form. Something with submit action being triggered..
        setUnlockButtonClicked(true);
        if (!whatsAppTemplateReplyRequired) {
            setAllowInputInExternalReply(true);
        }
    };

    const inputLockState = useInputLockState({
        tabSelected,
        allowInputInExternalReply,
        assignee,
        showWhatsAppInactivityTemplateState,
        ownExtensionKey,
    });

    const {
        fileInputRef,
        handleDrop,
        handleAddAttachment,
        handleRemoveAttachment,
        handlePasteImage,
        onAttachmentAspectRatioFound,
    } = useAttachmentHandler({
        setMessageContentInternal,
        setMessageContentReplyExternal,
        tabSelected,
        allowInputInExternalReply,
        externalChannel: externalChannelSelected,
        userAllowedToSendAttachments,
        allowedAttachmentsData,
        blockedMimeTypes: blockedAttachmentsMimeTypes,
    });

    const shouldDisplayEmailBanner = useMemo(
        () =>
            originSubtype === 'email' &&
            tabSelected === 'reply_external' &&
            allowInputInExternalReply,
        [originSubtype, tabSelected, allowInputInExternalReply],
    );

    const emailSignaturesEnabled = useAppSelector(selectEmailSignaturesEnabled);
    const defaultPremadeAnswer = useDefaultPremadeAnswer();

    useEffect(() => {
        if (
            shouldApplyDefaultSignature({
                emailSignaturesEnabled,
                tabSelected,
                originSubtype,
                defaultSignature: defaultPremadeAnswer?.message,
                messageContent: {
                    emailSignature: messageContentReplyExternal.emailSignature,
                    emailSignatureDismissed:
                        messageContentReplyExternal.emailSignatureDismissed,
                },
            })
        ) {
            setMessageContentReplyExternal((prev) => ({
                ...prev,
                emailSignature: defaultPremadeAnswer?.message,
            }));
        }
        // The two signature fields rather than the message object: the object
        // is new on every keystroke, which would run this on each one.
    }, [
        emailSignaturesEnabled,
        tabSelected,
        originSubtype,
        defaultPremadeAnswer,
        setMessageContentReplyExternal,
        messageContentReplyExternal.emailSignature,
        messageContentReplyExternal.emailSignatureDismissed,
    ]);

    const handleSelectPremadeAnswer = useCallback(
        (answer: PremadeAnswer) => {
            setMessageContentReplyExternal((prev) => ({
                ...prev,
                emailSignature: answer.message,
                emailSignatureDismissed: false,
            }));
        },
        [setMessageContentReplyExternal],
    );

    const handleRemoveSignature = useCallback(() => {
        setMessageContentReplyExternal((prev) => ({
            ...prev,
            emailSignature: undefined,
            emailSignatureDismissed: true,
        }));
        // The button removes itself along with the signature, so focus would
        // otherwise fall to <body>. The reply being written is the nearest
        // thing to carry on from.
        inputFieldRef.current?.focus();
    }, [setMessageContentReplyExternal]);

    const isEmailExternalReply =
        originSubtype === 'email' && tabSelected === 'reply_external';

    return (
        <div
            className="flex w-full flex-col"
            onDrop={(event) => {
                if (
                    tabSelected === 'reply_external' &&
                    !allowInputInExternalReply
                ) {
                    event.preventDefault();
                    event.stopPropagation();
                } else {
                    handleDrop(event);
                }
            }}
        >
            <TicketInputAttachmentPreview
                attachment={activeMessageContent.attachment}
                imageSrc={activeMessageContent.imageSrc}
                onClickRemove={handleRemoveAttachment}
                onAspectRatioFound={(aspectRatio) =>
                    onAttachmentAspectRatioFound(aspectRatio, tabSelected)
                }
            />
            <div onClick={setMessageFieldFocus}>
                {showMentionPicker ? (
                    <TicketMentionPicker
                        ticketKey={ticketKey}
                        text={getPossibleMentionText()}
                        onCloseRequested={closeMentionPicker}
                        onSelect={handleMentionAutoComplete}
                        ref={mentionPickerRef}
                    />
                ) : null}
                {shouldDisplayEmailBanner ? (
                    <BannerEmailRecipient ticketKey={ticketKey} />
                ) : null}
                <form>
                    <div
                        className={twMerge(
                            'relative flex min-h-16 w-full items-center gap-x-3 rounded-sm px-3 py-4',
                            tabSelected === 'internal'
                                ? 'bg-[#FCF2D8] dark:bg-[#332E1A]'
                                : 'bg-gray-200',
                            shouldDisplayEmailBanner ? 'rounded-t-none' : '',
                        )}
                    >
                        {inputLockState.shouldShowLockIndicator && (
                            <InputLockIndicator
                                showWhatsAppInactivityTemplateState={
                                    showWhatsAppInactivityTemplateState
                                }
                                assignee={assignee}
                                ownExtensionKey={ownExtensionKey}
                                onClickUnlock={onClickUnlockButton}
                            />
                        )}
                        {userAllowedToSendAttachments &&
                            !inputLockState.isLocked &&
                            channelSupportsAttachments && (
                                <AttachmentButton
                                    fileInputRef={fileInputRef}
                                    onAddAttachment={handleAddAttachment}
                                    disabled={attachmentButtonDisabled}
                                />
                            )}
                        <textarea
                            className={twMerge(
                                'block max-h-24 w-full resize-none overflow-y-scroll bg-transparent pt-1 text-sm text-wrap text-gray-800 antialiased placeholder:text-gray-500 focus:border-transparent focus:ring-0 focus:outline-hidden',
                                inputLockState.additionalCssClass,
                            )}
                            ref={inputFieldRef}
                            autoFocus={autoFocus}
                            disabled={inputDisabled}
                            onChange={handleTextChanged}
                            onKeyDown={handleKeyDown}
                            onClick={onClickTextField}
                            value={activeMessageContent.text}
                            aria-label={activePlaceholder}
                            placeholder={placeholder}
                            onPaste={handlePasteImage}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                                if (
                                    tabSelected === 'reply_external' &&
                                    !allowInputInExternalReply
                                ) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }
                            }}
                        />
                        <Suspense fallback={<></>}>
                            <EmojiSearch
                                textAreaRef={inputFieldRef}
                                messageContent={activeMessageContent}
                                updateMessage={(text, caretPosition) =>
                                    updateMessageAndCaretPosition(
                                        text,
                                        caretPosition,
                                        tabSelected,
                                    )
                                }
                            />
                        </Suspense>
                        {!inputLockState.isLocked && (
                            <EmojiPickerTickets
                                textAreaRef={inputFieldRef}
                                messageContent={activeMessageContent}
                                updateMessage={(text, caretPosition) =>
                                    updateMessageAndCaretPosition(
                                        text,
                                        caretPosition,
                                        tabSelected,
                                    )
                                }
                                disabled={
                                    tabSelected === 'reply_external' &&
                                    !allowInputInExternalReply
                                }
                            />
                        )}
                        <ComposerLoadingPlaceholder
                            loading={whatsAppEventsLoading}
                        />
                    </div>
                </form>
                {isEmailExternalReply &&
                    messageContentReplyExternal.emailSignature && (
                        <EmailSignaturePreview
                            signature={
                                messageContentReplyExternal.emailSignature
                            }
                            onRemove={handleRemoveSignature}
                        />
                    )}
            </div>
            <InputFooterV2
                ticketKey={ticketKey}
                tabSelected={tabSelected}
                tabSelectedIsWritable={tabSelectedIsWritable}
                canReply={canReply}
                canAddInternalNote={canAddInternalNote}
                originPending={originPending}
                replyUnsupported={replyUnsupported}
                onClickTab={onClickTab}
                messageContent={activeMessageContent}
                unlockButtonClicked={unlockButtonClicked}
                sendMessage={sendMessage}
                setExternalChannelSelected={setExternalChannelSelected}
                onSelectPremadeAnswer={
                    emailSignaturesEnabled
                        ? handleSelectPremadeAnswer
                        : undefined
                }
            />
        </div>
    );
};

export default MessageInputTicketV2;
